import { EnvironmentId, RELAY_FULL_CONTROL_SCOPES } from "@cmmd-forge/contracts";
import {
  createKnownEnvironment,
  createRelayRpcProtocolLayer,
  makeRelayWsRpcProtocolClient,
} from "@cmmd-forge/client-runtime";
import { readPrimaryEnvironmentTarget } from "../primary";
import { cmmdApiClient } from "../cmmdApi";
import {
  buildCurrentControlDeviceRegistration,
  getStoredLocalDeviceKey,
} from "../../deviceRegistryIdentity";
import { createWsRpcClient } from "../../rpc/wsRpcClient";
import { WsTransport, type WsTransportProtocol } from "../../rpc/wsTransport";
import { createEnvironmentConnection, type EnvironmentConnection } from "./connection";
import { createEnvironmentConnectionHandlers } from "./environmentConnectionHandlers";
import { getRelayControlJournal } from "./relayControlJournal";
import { createRelayControlRouteProvider } from "./relayControlRouteProvider";
import {
  setRuntimeConnected,
  setRuntimeConnecting,
  setRuntimeDisconnected,
  setRuntimeError,
  setRuntimeServerConfig,
} from "./runtimeConnectionStatus";
import { useSavedEnvironmentRuntimeStore } from "./savedEnvironmentRuntimeStore";

type RelayRuntimeDependencies = {
  readonly readConnection: (environmentId: EnvironmentId) => EnvironmentConnection | null;
  readonly registerConnection: (connection: EnvironmentConnection) => EnvironmentConnection;
};

const relayConnectionPromises = new Map<EnvironmentId, Promise<EnvironmentConnection>>();

async function connectRelayRuntimeEnvironment(
  input: { readonly runtimeId: string; readonly label: string },
  dependencies: RelayRuntimeDependencies,
): Promise<EnvironmentConnection> {
  const environmentId = EnvironmentId.make(input.runtimeId);
  const existing = dependencies.readConnection(environmentId);
  if (existing) return existing;
  setRuntimeConnecting(environmentId);
  const journal = getRelayControlJournal();
  const relayProvider = createRelayControlRouteProvider(input.runtimeId, {
    apiClient: cmmdApiClient,
    getStoredDeviceKey: getStoredLocalDeviceKey,
    buildDeviceRegistration: buildCurrentControlDeviceRegistration,
    controlSessionId: journal.controlSessionId,
  });
  let initialRoute: Awaited<ReturnType<typeof relayProvider.acquireRoute>>;
  try {
    initialRoute = await relayProvider.acquireRoute();
  } catch (error) {
    setRuntimeError(environmentId, error);
    throw error;
  }
  let firstRoute: typeof initialRoute | null = initialRoute;
  const protocolLayer = createRelayRpcProtocolLayer({
    acquireRoute: async () => {
      const route = firstRoute;
      firstRoute = null;
      return route ?? relayProvider.acquireRoute();
    },
    acquireActionGrant: relayProvider.acquireActionGrant,
    journal,
    lifecycle: {
      onConnecting: () => setRuntimeConnecting(environmentId),
      onAuthenticated: () => setRuntimeConnected(environmentId),
      onDisconnected: (details) => setRuntimeDisconnected(environmentId, details.reason),
      onError: (details) =>
        setRuntimeError(environmentId, Object.assign(new Error(details.message), details)),
    },
  });
  const client = createWsRpcClient(
    new WsTransport(
      initialRoute.relayWebSocketUrl,
      { trackGlobalConnection: false },
      {
        layer: protocolLayer,
        makeClient: makeRelayWsRpcProtocolClient(
          RELAY_FULL_CONTROL_SCOPES,
        ) as unknown as WsTransportProtocol["makeClient"],
      },
    ),
  );
  const knownEnvironment = createKnownEnvironment({
    id: environmentId,
    label: input.label,
    source: "manual",
    target: {
      httpBaseUrl:
        readPrimaryEnvironmentTarget()?.target.httpBaseUrl ?? initialRoute.relayWebSocketUrl,
      wsBaseUrl: initialRoute.relayWebSocketUrl,
    },
  });
  const connection = createEnvironmentConnection({
    kind: "saved",
    knownEnvironment: { ...knownEnvironment, environmentId },
    client,
    tolerateReportedEnvironmentIdentity: true,
    onConfigSnapshot: (config) => setRuntimeServerConfig(environmentId, config),
    ...createEnvironmentConnectionHandlers((payload) => {
      useSavedEnvironmentRuntimeStore
        .getState()
        .patch(environmentId, { descriptor: payload.environment });
    }),
  });
  try {
    const [, serverConfig] = await Promise.all([
      connection.ensureBootstrapped(),
      client.server.getConfig(),
    ]);
    setRuntimeServerConfig(environmentId, serverConfig);
    setRuntimeConnected(environmentId);
    return dependencies.registerConnection(connection);
  } catch (error) {
    setRuntimeError(environmentId, error);
    await connection.dispose().catch(() => undefined);
    throw error;
  }
}

export function createRelayRuntimeEnvironmentConnection(
  input: { readonly runtimeId: string; readonly label: string },
  dependencies: RelayRuntimeDependencies,
): Promise<EnvironmentConnection> {
  const environmentId = EnvironmentId.make(input.runtimeId);
  const existing = dependencies.readConnection(environmentId);
  if (existing) return Promise.resolve(existing);
  const pending = relayConnectionPromises.get(environmentId);
  if (pending) return pending;
  const connection = connectRelayRuntimeEnvironment(input, dependencies).finally(() => {
    relayConnectionPromises.delete(environmentId);
  });
  relayConnectionPromises.set(environmentId, connection);
  return connection;
}

export function resetRelayRuntimeConnectionForTests(): void {
  relayConnectionPromises.clear();
}
